import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { PraksisaktivitetInfoDto } from '../../api/generated/studiested-generated-api';
import { ConfirmationDialog } from '../../components/dialog';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../../components/table/alpTable';
import { dateWithLocale } from '../../date.utils';
import { sortNotInPlace } from '../../util/sort';
import { Language } from '../../i18n/LanguageStore';
import translations from './addpracticeplaceagreement.i18n';
import * as css from './agreementactivitieswarning.module.scss';

export const getPeriod = (
  locale: string,
  startDato: string,
  sluttDato: string,
): string =>
  `${dateWithLocale(
    new Date(startDato),
    locale as Language,
  )} - ${dateWithLocale(new Date(sluttDato), locale as Language)}`;

enum AktiviteterListHeader {
  PERIOD = 'PERIOD',
  EMNENAVN = 'EMNENAVN',
  EMNEBESKRIVELSE = 'EMNEBESKRIVELSE',
  PRAKSISTYPE = 'PRAKSISTYPE',
  PRAKSISKLASSE = 'PRAKSISKLASSE',
  BESTILLER = 'BESTILLER',
  HANDLING = 'HANDLING',
}

type AktivitetHeader = {
  title: string;
  canSort: boolean;
};

type AktiviteterListHeaders = {
  [AktiviteterListHeader.PERIOD]: AktivitetHeader;
  [AktiviteterListHeader.EMNENAVN]: AktivitetHeader;
  [AktiviteterListHeader.EMNEBESKRIVELSE]: AktivitetHeader;
  [AktiviteterListHeader.PRAKSISTYPE]: AktivitetHeader;
  [AktiviteterListHeader.PRAKSISKLASSE]: AktivitetHeader;
  [AktiviteterListHeader.BESTILLER]: AktivitetHeader;
  [AktiviteterListHeader.HANDLING]: AktivitetHeader;
};

type AgreementAktivitetRowProps = {
  aktivitet: PraksisaktivitetInfoDto;
  headers: AktiviteterListHeaders;
  labelVisAktivitet: string;
};

const AgreementAktivitetRow = React.memo<AgreementAktivitetRowProps>(
  ({ aktivitet, headers, labelVisAktivitet }) => {
    const { locale } = useIntl();
    const {
      id,
      startDato,
      sluttDato,
      emne: { navn: emneNavn, kode: emneKode },
      praksis: { navn: praksisNavn },
      klasse: { bokstav: klasseBokstav },
      sted: { navn: stedNavn },
    } = aktivitet;

    const period = getPeriod(locale, startDato, sluttDato);

    return (
      <TableRow>
        <TableCellTd header={headers[AktiviteterListHeader.PERIOD].title}>
          {period}
        </TableCellTd>
        <TableCellTd header={headers[AktiviteterListHeader.EMNENAVN].title}>
          {emneNavn}
        </TableCellTd>
        <TableCellTd
          header={headers[AktiviteterListHeader.EMNEBESKRIVELSE].title}
        >
          {emneKode}
        </TableCellTd>
        <TableCellTd header={headers[AktiviteterListHeader.PRAKSISTYPE].title}>
          {praksisNavn}
        </TableCellTd>
        <TableCellTd
          header={headers[AktiviteterListHeader.PRAKSISKLASSE].title}
        >
          {klasseBokstav}
        </TableCellTd>
        <TableCellTd header={headers[AktiviteterListHeader.BESTILLER].title}>
          {stedNavn.join(', ')}
        </TableCellTd>
        <TableCellTd header={headers[AktiviteterListHeader.HANDLING].title}>
          <Link to={`/calendar/activity/${id}/`} target={'_blank'}>
            {labelVisAktivitet}
          </Link>
        </TableCellTd>
      </TableRow>
    );
  },
);

type AgreementAktiviteterProps = {
  aktiviteter: PraksisaktivitetInfoDto[];
};

const AgreementAktiviteterList: React.FC<
  React.PropsWithChildren<AgreementAktiviteterProps>
> = ({ aktiviteter }) => {
  const { formatMessage, locale } = useIntl();
  const labelVisAktivitet = formatMessage({
    id: translations.aktiviteterVisAktivitet.id,
  });
  const tableHeaders = useMemo(
    () => ({
      [AktiviteterListHeader.PERIOD]: {
        title: formatMessage({
          id: translations.aktiviteterHeaderPeriod.id,
        }),
        canSort: true,
      },
      [AktiviteterListHeader.EMNENAVN]: {
        title: formatMessage({
          id: translations.aktiviteterHeaderEmnenavn.id,
        }),
        canSort: true,
      },
      [AktiviteterListHeader.EMNEBESKRIVELSE]: {
        title: formatMessage({
          id: translations.aktiviteterHeaderEmnebeskrivelse.id,
        }),
        canSort: true,
      },
      [AktiviteterListHeader.PRAKSISTYPE]: {
        title: formatMessage({
          id: translations.aktiviteterHeaderPraksistype.id,
        }),
        canSort: true,
      },
      [AktiviteterListHeader.PRAKSISKLASSE]: {
        title: formatMessage({
          id: translations.aktiviteterHeaderPraksisklasse.id,
        }),
        canSort: true,
      },
      [AktiviteterListHeader.BESTILLER]: {
        title: formatMessage({
          id: translations.aktiviteterHeaderBestiller.id,
        }),
        canSort: true,
      },
      [AktiviteterListHeader.HANDLING]: {
        title: formatMessage({
          id: translations.aktiviteterHeaderHandling.id,
        }),
        canSort: false,
      },
    }),
    [formatMessage],
  );

  const sort = useCallback(
    (col: number, a: PraksisaktivitetInfoDto, b: PraksisaktivitetInfoDto) => {
      const compare = Intl.Collator(locale || 'nb').compare;
      switch (Object.keys(tableHeaders)[col]) {
        case AktiviteterListHeader.PERIOD:
          return a.startDato !== b.startDato
            ? compare(
                Date.parse(a.startDato).toString(),
                Date.parse(b.startDato).toString(),
              )
            : compare(
                Date.parse(a.sluttDato).toString(),
                Date.parse(b.sluttDato).toString(),
              );
        case AktiviteterListHeader.EMNENAVN:
          return compare(a.emne.navn, b.emne.navn);
        case AktiviteterListHeader.EMNEBESKRIVELSE:
          return compare(a.emne.kode, b.emne.kode);
        case AktiviteterListHeader.PRAKSISTYPE:
          return compare(a.praksis.navn, b.praksis.navn);
        case AktiviteterListHeader.PRAKSISKLASSE:
          return compare(a.klasse.bokstav, b.klasse.bokstav);
        case AktiviteterListHeader.BESTILLER:
          return compare(a.sted.navn.join(', '), b.sted.navn.join(', '));
      }
      return 0;
    },
    [locale, tableHeaders],
  );

  const [activeCol, setActiveCol] = useState(0);
  const [sortAscending, setSortAscending] = useState(false);
  const aktiviteterSorted = useMemo(
    () =>
      sortNotInPlace(aktiviteter, (a, b) =>
        sortAscending ? sort(activeCol, b, a) : sort(activeCol, a, b),
      ),
    [aktiviteter, activeCol, sortAscending, sort],
  );

  const sortActivities = useCallback(
    (col: number, ascending: boolean) => {
      setActiveCol(col);
      setSortAscending(ascending);
    },
    [setActiveCol, setSortAscending],
  );

  const headers = useMemo(
    () =>
      Object.entries(tableHeaders).map(([type, { title, canSort }], index) => (
        <TableCellTh
          key={`aktiviteter-header-${type}`}
          colIndex={index}
          activeCol={index === activeCol}
          setActiveCol={setActiveCol}
          sort={sortActivities}
          canSort={canSort}
        >
          {title}
        </TableCellTh>
      )),
    [tableHeaders, activeCol, setActiveCol, sortActivities],
  );

  const rows = useMemo(
    () =>
      aktiviteterSorted.map((aktivitet) => (
        <AgreementAktivitetRow
          key={aktivitet.id}
          aktivitet={aktivitet}
          headers={tableHeaders}
          labelVisAktivitet={labelVisAktivitet}
        />
      )),
    [aktiviteterSorted, tableHeaders, labelVisAktivitet],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow top>{headers}</TableRow>
      </TableHeader>
      <TableMain>{rows}</TableMain>
    </Table>
  );
};

type AgreementAktiviteterWarningModalProps = {
  onClose: (confirmed: boolean) => void;
  aktiviteter: PraksisaktivitetInfoDto[];
};

export const AgreementAktiviteterWarningModal: React.FC<
  React.PropsWithChildren<AgreementAktiviteterWarningModalProps>
> = ({ onClose, aktiviteter }) => {
  const { formatMessage } = useIntl();
  const labels = {
    continue: formatMessage({ id: translations.continue.id }),
    cancel: formatMessage({ id: translations.cancel.id }),
    avtaleAktiviteterWarningModalTitle: formatMessage({
      id: translations.avtaleAktiviteterWarningModalTitle.id,
    }),
    avtaleAktiviteterWarningModalSubtitle: formatMessage({
      id: translations.avtaleAktiviteterWarningModalSubtitle.id,
    }),
  };

  return (
    <ConfirmationDialog
      isOpen
      title={labels.avtaleAktiviteterWarningModalTitle}
      subtitle={labels.avtaleAktiviteterWarningModalSubtitle}
      submitLabel={labels.continue}
      cancelLabel={labels.cancel}
      onClose={onClose}
      extraClasses={[css.agreementActivitesWarningModal]}
      dataCy={'avtaleAktiviteterWarningModal'}
    >
      <AgreementAktiviteterList aktiviteter={aktiviteter} />
    </ConfirmationDialog>
  );
};
